import { formatDate } from '@privacy-bee/format/date';

/**
 * The calendar day a billing value names, in the `YYYY-MM-DD` shape
 * `@privacy-bee/format` pins to UTC.
 *
 * A renewal date, an invoice date and a quote expiry are calendar days, but the
 * API serializes several of them as instants ("2027-04-27T00:00:00.000000Z").
 * Rendering that instant in the viewer's zone moves it to the 26th everywhere
 * west of UTC — a different billing day than the one the invoice states, for
 * every customer in the United States.
 *
 * A value that does not open with a calendar day is passed through untouched,
 * so `formatDate` still judges it.
 */
function calendarDay(value: string | null | undefined): string {
    if (!value) return '';
    return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : value;
}

/**
 * A renewal, invoice or expiry date as the customer reads it: "Apr 27, 2027".
 *
 * The day comes from the value itself and the writing comes from `@privacy-bee/format`,
 * which is what keeps one billing day one string in every time zone. An instant
 * whose moment is genuinely the question — when a key was created, when a
 * session was last seen — is not a billing date and calls `formatDate`
 * directly, so it renders in the viewer's own zone.
 *
 * A missing or unparseable value gives `''`: the words a surface shows for a
 * date it does not have are that surface's copy, not this function's.
 */
export function formatBillingDate(value: string | null | undefined): string {
    return formatDate(calendarDay(value));
}

/** A calendar day's parts: its year, its month (1 to 12) and its day of the month. */
export interface CalendarDayParts {
    year: number;
    month: number;
    day: number;
}

/**
 * The calendar day a value names, as its parts, read the way `calendarDay` above
 * reads it: a `YYYY-MM-DD` string or an instant that opens with one gives that
 * day, whatever the reader's zone.
 *
 * `null` when the value names no day.
 */
export function calendarDayParts(value: string | null | undefined): CalendarDayParts | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(calendarDay(value));

    return match ? { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) } : null;
}

const DAY_MS = 86_400_000;

/**
 * How many calendar days from the reader's today to `day`: 0 on the day
 * itself, negative once it has passed.
 *
 * The count is taken against the reader's own calendar day, because that is
 * the day they mean. Both sides are reduced to a day before they are
 * subtracted, so a day that is tomorrow never counts as 0 because the clock
 * happens to be past noon.
 */
export function daysUntilCalendarDay(day: CalendarDayParts, now: Date = new Date()): number {
    const target = Date.UTC(day.year, day.month - 1, day.day);
    const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());

    return Math.round((target - today) / DAY_MS);
}

/**
 * How many calendar days from the reader's today to the day a value names
 * (`calendarDayParts`, then `daysUntilCalendarDay`).
 *
 * `null` when the value names no day: the words a surface shows for a day it
 * does not have are that surface's copy, not this function's.
 */
export function calendarDaysUntil(value: string | null | undefined, now: Date = new Date()): number | null {
    const day = calendarDayParts(value);

    return day ? daysUntilCalendarDay(day, now) : null;
}

/**
 * How long is left to pay, as an invoice row says it: "3 days left", "Due
 * today", "2 days overdue", counted in calendar days (`calendarDaysUntil`).
 *
 * `null` when the value names no day: the words a row shows for a due date it
 * does not have are that row's copy, not this function's.
 */
export function billingDaysLeft(value: string | null | undefined, now: Date = new Date()): string | null {
    const days = calendarDaysUntil(value, now);
    if (days === null) return null;

    if (days === 0) return 'Due today';
    if (days > 0) return `${days} ${days === 1 ? 'day' : 'days'} left`;

    return `${-days} ${days === -1 ? 'day' : 'days'} overdue`;
}
